import { getResourceTypeDetails, ResourceTypeDetails } from 'src/utils/resource-types'
import {
  ResourceStatus,
  ViewableApplicationResource,
} from '../account/components/app-resources-view/app-resources-view.component'
import {
  ApplicationResourceDto,
  ApplicationResourceStatusDto,
  ApplicationSourceResourceDto,
  ApplicationType,
  AwsTargetResourceDto,
  SyncPairResourceStateDto,
} from '../services/dtos'
import { computeResourceDisplayName } from './utils'
import { RelevantStatus } from './types'
import { RaisedIssue } from '../account/services/issue'

export function translateSyncPairResourceStateDto(state: SyncPairResourceStateDto) {
  switch (state) {
    case SyncPairResourceStateDto.INIT:
      return ResourceStatus.PENDING
    case SyncPairResourceStateDto.PROCESSING:
    case SyncPairResourceStateDto.UNSATISFIED_DEPENDENCY:
      return ResourceStatus.PROCESSING
    case SyncPairResourceStateDto.FAILURE:
      return ResourceStatus.FAILED
    case SyncPairResourceStateDto.SUCCESS:
      return ResourceStatus.SUCCEEDED
    case SyncPairResourceStateDto.QUARANTINED:
      return ResourceStatus.QUARANTINED
    case SyncPairResourceStateDto.SCAN_CLEAN:
      return ResourceStatus.SCAN_CLEAN
    case SyncPairResourceStateDto.SCAN_INFECTED:
      return ResourceStatus.SCAN_INFECTED
    case SyncPairResourceStateDto.SCAN_FAILED:
      return ResourceStatus.SCAN_FAILED
  }
  return ResourceStatus.NO_STATUS
}

function azurePortalUri(resourceId: string): string | undefined {
  if (!resourceId) return undefined
  return `https://portal.azure.com/#@/resource${resourceId}`
}

/**
 * ApplicationResource combines a source resource and a target resource into a single object.
 */
export class ApplicationResource implements ViewableApplicationResource {
  status: ResourceStatus
  progress: number
  message: string
  sourceRes: ApplicationSourceResourceDto
  sourceResName?: string
  targetResName = ''
  resourceTypeDetails: ResourceTypeDetails

  sourceResourcePortalUri?: string
  targetResourcePortalUri?: string
  resourcePortalName?: string

  constructor(
    relevantStatus: RelevantStatus,
    private appResource: ApplicationResourceDto,
    private targetRes: AwsTargetResourceDto | null,
    private latestStatus: ApplicationResourceStatusDto | null,
    public issues: Set<RaisedIssue>,
    private currentlyProcessing: boolean,
    private processingHorizon: Date,
    applicationType: ApplicationType = ApplicationType.AWS
  ) {
    this.sourceRes = appResource.source
    this.resourceTypeDetails = getResourceTypeDetails({
      resourceType: appResource.type,
      resourceVariant: appResource.variant,
    })
    ;[this.status, this.progress, this.message] = this._computeStatus(relevantStatus)

    if (applicationType === ApplicationType.AZURE) {
      this.sourceResourcePortalUri = azurePortalUri(this.sourceRes?.id)
      this.targetResourcePortalUri = azurePortalUri(this.targetRes?.id)
      this.resourcePortalName = 'Azure Portal'
    }
  }

  private _computeStatus(relevantStatus: RelevantStatus): [ResourceStatus, number, string] {
    let latestStatus = null

    if (relevantStatus & RelevantStatus.STAGING) {
      latestStatus = this.latestStatus || this.appResource.backupStatus
    } else if (relevantStatus & RelevantStatus.SYNC) {
      latestStatus = this.latestStatus || this.appResource.restoreStatus
    } else if (relevantStatus & RelevantStatus.FAILBACK) {
      latestStatus = this.latestStatus || this.appResource.failbackStatus
    }

    if (this.currentlyProcessing && latestStatus && latestStatus.timestamp > this.processingHorizon) {
      // We are processing and the latest status came in after we started processing
      const status = translateSyncPairResourceStateDto(latestStatus.state)
      return [status, latestStatus.progress, latestStatus.message]
    } else if (this.currentlyProcessing) {
      // We are processing and latest status is stale (from previous job).  Show pending status
      return [ResourceStatus.PENDING, 0, null]
    } else if (latestStatus && latestStatus.timestamp < this.processingHorizon) {
      // The latest status was produced prior to the most recently completed job.  Show no status.
      return [ResourceStatus.NO_STATUS, 0, null]
    } else if (latestStatus) {
      // Not processing and latest status is up-to-date. Just show the most recent status we have from previously
      // completed job
      let status = translateSyncPairResourceStateDto(latestStatus.state)

      if (status == ResourceStatus.PENDING) {
        // if the we aren't currently processing, but the resource state is PENDING, it
        // means that the processing never got to this resource (presumably because a dependee
        // failed to process).  Set the status to NO_STATUS in that case.
        status = ResourceStatus.NO_STATUS
      }

      return [status, latestStatus.progress, latestStatus.message]
    } else {
      // Not processing and no status available yet for the relevantStatus
      return [ResourceStatus.NO_STATUS, 0, null]
    }
  }

  get sourceResourceName() {
    /**
     * Combine the resourceName and the resourceId into a single field to show in the UI
     * if the resourceName exists and it isn't the same as the ID.
     */
    if (!this.sourceResName) {
      if (!this.sourceRes) {
        // If no source resource was returned, it means it was deleted from the source or we no longer have access to it.
        // In that case, we use an empty string as the display name.
        return ''
      }
      this.sourceResName = computeResourceDisplayName(
        this.sourceRes.id,
        this.sourceRes.name,
        this.sourceRes.displayName
      )
    }
    return this.sourceResName
  }

  get targetResourceName() {
    /**
     * Same as resourceDisplayName, but looks at the target resource.
     */
    if (!!this.targetRes && !this.targetResName) {
      this.targetResName = computeResourceDisplayName(
        this.targetRes.id,
        this.targetRes.name,
        this.targetRes.displayName
      )
    }
    return this.targetResName
  }

  get sourceResourceGroup() {
    return this.sourceRes?.resourceGroup
  }

  get targetResourceGroup() {
    return this.targetRes?.resourceGroup
  }

  get resourceProperties() {
    if (!!this.targetRes) {
      return this.targetRes.properties
    }
    return []
  }

  get resourceType() {
    return this.appResource.type
  }

  get resourceVariant() {
    return this.appResource.variant
  }

  get resourceArn() {
    return this.appResource.arn
  }

  get resourceId() {
    return this.sourceRes?.id || this.targetRes?.id || ''
  }

  get displayResourceType() {
    return this.resourceTypeDetails.displayName
  }
}
